import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Filter, PlusSquare, X } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ALL = "ALLE";

export default function Orders() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();

  const [f, setF] = useState({
    q: sp.get("q") || "",
    status: sp.get("status") || ALL,
    auftragstyp: sp.get("auftragstyp") || ALL,
    mitarbeiter: sp.get("mitarbeiter") || ALL,
    fahrzeug: sp.get("fahrzeug") || ALL,
    datum_von: sp.get("datum_von") || "",
    datum_bis: sp.get("datum_bis") || "",
  });
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
      const { data } = await api.get("/orders", { params });
      // client-side filter for auftragstyp (server does not currently filter it)
      const filtered = params.auftragstyp ? data.filter((o) => o.auftragstyp === params.auftragstyp) : data;
      setOrders(filtered);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sp]);

  const submit = (e) => {
    e.preventDefault();
    const np = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== ALL) np[k] = v; });
    setSp(np);
  };

  const reset = () => {
    const empty = { q: "", status: ALL, auftragstyp: ALL, mitarbeiter: ALL, fahrzeug: ALL, datum_von: "", datum_bis: "" };
    setF(empty);
    setSp({});
  };

  return (
    <div data-testid="orders-list">
      <PageHeader
        title="Aufträge"
        subtitle={`${orders.length} Ergebnisse`}
        actions={
          <Button
            data-testid="orders-new-btn"
            className="h-12 px-5 bg-[#0A2540] hover:bg-[#113A63] text-white"
            onClick={() => nav("/orders/new")}
          >
            <PlusSquare className="h-4 w-4 mr-2" /> Neuer Auftrag
          </Button>
        }
      />

      <form onSubmit={submit} className="bg-white border rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" style={{ borderColor: "#E2E8F0" }}>
        <div className="md:col-span-4">
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Suche</Label>
          <div className="relative mt-1.5">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="orders-search-input"
              value={f.q}
              onChange={(e) => setF({ ...f, q: e.target.value })}
              placeholder="Auftragsnr., KLS-ID, Telekom Konto, Bemerkung…"
              className="h-12 pl-10"
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</Label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Alle</SelectItem>
              <SelectItem value="Erfasst">Erfasst</SelectItem>
              <SelectItem value="Abgerechnet">Abgerechnet</SelectItem>
              <SelectItem value="Konflikt">Konflikt</SelectItem>
              <SelectItem value="Abgelehnt">Abgelehnt</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Auftragstyp</Label>
          <Select value={f.auftragstyp} onValueChange={(v) => setF({ ...f, auftragstyp: v })}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="orders-typ-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Alle</SelectItem>
              <SelectItem value="FTTH">FTTH</SelectItem>
              <SelectItem value="GWV">GWV</SelectItem>
              <SelectItem value="NE4">NE4</SelectItem>
              <SelectItem value="Mischauftrag">Mischauftrag</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mitarbeiter</Label>
          <Select value={f.mitarbeiter} onValueChange={(v) => setF({ ...f, mitarbeiter: v })}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="orders-mitarbeiter-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Alle</SelectItem>
              {users.map((u) => (<SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Fahrzeug</Label>
          <Select value={f.fahrzeug} onValueChange={(v) => setF({ ...f, fahrzeug: v })}>
            <SelectTrigger className="h-12 mt-1.5" data-testid="orders-fahrzeug-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Alle</SelectItem>
              {vehicles.map((v) => (<SelectItem key={v.id} value={v.id}>{v.kennzeichen}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Von</Label>
          <Input type="date" className="h-12 mt-1.5" value={f.datum_von} onChange={(e) => setF({ ...f, datum_von: e.target.value })} data-testid="orders-von" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Bis</Label>
          <Input type="date" className="h-12 mt-1.5" value={f.datum_bis} onChange={(e) => setF({ ...f, datum_bis: e.target.value })} data-testid="orders-bis" />
        </div>
        <div className="md:col-span-4 flex gap-2">
          <Button type="submit" className="h-12 px-6 bg-[#0A2540] hover:bg-[#113A63] text-white flex-1" data-testid="orders-search-btn">
            <Filter className="h-4 w-4 mr-2" /> Filtern
          </Button>
          <Button type="button" variant="outline" className="h-12" onClick={reset} data-testid="orders-reset-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </form>

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Auftragsnr.</th>
                <th className="text-left px-4 py-3 font-semibold">KLS-ID</th>
                <th className="text-left px-4 py-3 font-semibold">Datum</th>
                <th className="text-left px-4 py-3 font-semibold">Typ</th>
                <th className="text-left px-4 py-3 font-semibold">PL</th>
                <th className="text-left px-4 py-3 font-semibold">Mitarbeiter</th>
                <th className="text-left px-4 py-3 font-semibold">Telekom</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Aufmaß</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="py-10 text-center text-slate-400">Lädt…</td></tr>}
              {!loading && orders.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-slate-400">Keine Aufträge gefunden</td></tr>}
              {orders.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`order-row-${o.auftragsnummer}`}
                  className="border-t hover:bg-slate-50 cursor-pointer"
                  style={{ borderColor: "#E2E8F0" }}
                  onClick={() => nav(`/orders/${o.id}`)}
                >
                  <td className="px-4 py-3 font-semibold">{o.auftragsnummer}</td>
                  <td className="px-4 py-3 mono">{o.kls_id}</td>
                  <td className="px-4 py-3 tabular-nums">{o.ausfuehrungsdatum}</td>
                  <td className="px-4 py-3">{o.auftragstyp}</td>
                  <td className="px-4 py-3 tabular-nums font-semibold">P{o.preisliste}</td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-[180px]">
                    {[o.mitarbeiter_1, o.mitarbeiter_2, o.mitarbeiter_3, o.mitarbeiter_4].filter(Boolean).join(", ")}
                  </td>
                  <td className="px-4 py-3">{o.telekom_konto}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-right tabular-nums">€ {(o.aufmass_total || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ActionBar
        onExport={async () => {
          const r = await api.get("/export/orders.csv", { responseType: "blob" });
          const url = window.URL.createObjectURL(r.data);
          const a = document.createElement("a"); a.href = url; a.download = "auftraege.csv"; a.click();
        }}
        onPrint={() => window.print()}
        hideNext
      />
    </div>
  );
}
